import { getISOWeek } from 'date-fns'

export const groupBy = (input, key) => {
  return input.reduce((acc, currentValue) => {
    let groupKey = currentValue[key]

    if (!acc[groupKey]) {
      acc[groupKey] = []
    }

    acc[groupKey].push(currentValue)
    return acc
  }, {})
}

// Texto de un error cualquiera: el que manda el servidor ({ message } o { error }), un fallo de
// red de RTK Query (err.error), el de una excepción, o como último recurso el código HTTP.
// Antes muchas pantallas pasaban `err.message`, que en un error de RTK Query no existe, y el
// usuario solo veía un "Error" genérico.
export const mensajeDeError = (err) => {
  if (!err) return null
  if (typeof err === 'string') return err
  const d = err.data
  if (typeof d === 'string' && d.trim()) return d
  if (d && typeof d === 'object') {
    const texto = d.message || d.error || d.detail
    if (texto) return String(texto)
  }
  if (err.error) return String(err.error)
  if (err.message) return String(err.message)
  if (err.status) return `HTTP ${err.status}`
  return null
}

// Mensaje de error legible a partir de un fallo de RTK Query. Cuando la respuesta se pide
// como blob (descargas), el cuerpo del error también llega como blob, así que hay que leerlo
// para poder enseñar algo que no sea "Error".
export const readApiError = async (err) => {
  let data = err?.data
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    try {
      data = JSON.parse(await data.text())
    } catch {
      data = null
    }
  }
  if (!data) return { code: null, text: err?.error || err?.message || null, data: null }
  const code = data.error || null
  const partes = [data.message, data.detail, data.of ? `OF ${data.of}` : null]
  if (data.upstreamStatus) partes.push(`HTTP ${data.upstreamStatus}`)
  return { code, text: partes.filter(Boolean).join(' · ') || code, data }
}

// Clave normalizada de un centro de trabajo. En BAAN el centro se guarda relleno a 3 ('043')
// y bond_center_manager lo tiene sin rellenar ('43'), así que comparar en crudo deja el
// desplegable vacío al editar un bono (y con él se pierden OF y fase). Los centros de Novak
// no son numéricos (AJUSTE, BF01): esos se comparan como texto.
export const centerKey = (v) => {
  const s = String(v ?? '').trim()
  return /^\d+$/.test(s) ? String(Number(s)) : s.toUpperCase()
}

// Corta el intervalo de un bono en N tramos iguales y contiguos (el fin de uno es el inicio
// del siguiente). Los cortes se redondean al minuto porque el selector no tiene segundos, y
// el último tramo termina exactamente en el fin original para que no se pierda tiempo.
// Devuelve pares de Date; el formateo lo pone quien llama.
export const equalSplit = (start, end, parts) => {
  const ini = new Date(start).getTime()
  const fin = new Date(end).getTime()
  const n = Math.floor(Number(parts) || 0)
  if (!Number.isFinite(ini) || !Number.isFinite(fin) || fin <= ini || n < 2) return []
  const corte = (i) => Math.round((ini + ((fin - ini) * i) / n) / 60000) * 60000
  return Array.from({ length: n }, (_, i) => ({
    start: new Date(i === 0 ? ini : corte(i)),
    end: new Date(i === n - 1 ? fin : corte(i + 1)),
  }))
}

// Enlace del pedido a la extranet de compras. Solo cuando la OF tiene pedido de venta y su
// cliente es una empresa del grupo: mismo criterio y mismos prefijos que el legacy
// (asociacion_cltebaan_prefijoextranet en fabricationorders/Production.php). Para el resto de
// clientes el pedido se queda en texto, porque su pedido no existe en la extranet.
const EXTRANET_PREFIJO = {
  100000001: '108', // DANOBAT ELGOIBAR KOOP.
  100000131: '210', // SORALUCE S.COOP.
}

export const extranetOrderUrl = ({
  sales_order: ov,
  purchase_order: pedido,
  customer_code,
} = {}) => {
  const prefijo = EXTRANET_PREFIJO[String(customer_code ?? '').trim()]
  const orden = String(pedido ?? '').trim()
  if (!prefijo || !orden || !String(ov ?? '').trim()) return null
  return `https://erosketak.danobatgroup.com/orders/${prefijo}/${encodeURIComponent(orden)}`
}

// Vuelta a la ficha del fundido en Sutan (llegan de las etiquetas QR viejas con ?from=sutan).
// Mismo id en los dos sistemas: comparten bf_production_rfid. `?from=mes` es obligatorio: sin él,
// el hook de sutan-ln vuelve a redirigir a MES y se monta un bucle.
const SUTAN_IDIOMAS = ['es', 'eu', 'en', 'de']
export const sutanRfidUrl = (id, language) => {
  const base = process.env.REACT_APP_SUTAN_URL
  if (!base || id == null) return null
  const lang = String(language ?? '').slice(0, 2)
  const idioma = SUTAN_IDIOMAS.includes(lang) ? lang : 'es'
  return `${base.replace(/\/+$/, '')}/${idioma}/production/rfid/edit/${encodeURIComponent(id)}?from=mes`
}

// Ficha de la incidencia en Sutan, como el enlace junto al selector de bonds_validation: la 924
// (tipo 9) es de TPM y el resto de calidad. `id` es el de bf_incidence, no el sid.
export const sutanIncidenceUrl = (id, type, language) => {
  const base = process.env.REACT_APP_SUTAN_URL
  if (!base || id == null) return null
  const lang = String(language ?? '').slice(0, 2)
  const idioma = SUTAN_IDIOMAS.includes(lang) ? lang : 'es'
  const modulo = Number(type) === 9 ? 'tpm' : 'qbc'
  return `${base.replace(/\/+$/, '')}/${idioma}/${modulo}/incidence/edit/${encodeURIComponent(id)}`
}

// Centro de trabajo efectivo de una operacion: el cambiado en MES (ttidan965.t_cwoc) manda sobre
// el de BAAN. Mismo criterio que usa el filtro del servidor: ISNULL(ttidan965.t_cwoc, ttirou001.t_cwoc).
export const effectiveWorkCenter = (row = {}) => {
  const cambiado = String(row.changedWorkCenter ?? '').trim()
  return cambiado && cambiado !== '0' ? cambiado : row.working_center_id
}

// El centro de trabajo de una OF se puede cambiar en MES (ttidan965) a uno que no esté en la ruta
// del artículo en BAAN. El selector se alimenta de la ruta, así que hay que añadir el centro actual
// o se quedaría vacío al abrir el programa.
export const ensureWorkCenterOption = (options, workCenter) => {
  const actual = String(workCenter ?? '').trim()
  if (!actual || options.some((option) => String(option.value).trim() === actual)) {
    return options
  }
  return [...options, { value: actual, label: actual, selected: true }]
}

// Semana ISO (empieza en lunes) con dos dígitos, como la lista de capacidad y LN.
export const isoWeekLabel = (date) => String(getISOWeek(date)).padStart(2, '0')

// El API documental devuelve la ruta local del servidor (e:\Datuak_E\...); al usuario se le
// muestra/copia la ruta que abre en su equipo: Dokumentazioa está mapeada como M:, el resto
// por ruta de red compartida.
export const networkPath = (path) =>
  String(path || '')
    .replace(/^e:\\Datuak_E\\Dokumentazioa\\/i, 'M:\\')
    .replace(/^e:\\Datuak_E\\/i, '\\\\giz-datuak\\')

// Soraluce (ITDG-4201): con detección MECANIZADO solo se ofrecen las causas de Producción; con
// cualquier otra, las de Calidad. Misma regla que actividadDeCausa del servidor.
export const causasParaDeteccion = (causes = [], detectionName = '') => {
  const actividad = /^mecanizado$/i.test(String(detectionName).trim()) ? 'production' : 'qbc'
  return causes.filter((c) => c.activity === actividad)
}

// Con MES abierto durante un despliegue, el navegador sigue con la versión vieja y al abrir una
// pantalla pide un fichero (chunk) que ya no existe: React deja la pantalla en blanco. Se recarga
// para coger la versión nueva; como mucho una vez cada 10 s para no entrar en bucle si el fallo
// es de red.
export const recargarSiFaltaChunk = (
  error,
  storage = sessionStorage,
  recargar = () => window.location.reload(),
) => {
  if (error?.name !== 'ChunkLoadError') return false
  const ultima = Number(storage.getItem('chunkReload')) || 0
  if (Date.now() - ultima < 10000) return false
  storage.setItem('chunkReload', String(Date.now()))
  recargar()
  return true
}
